import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ScreenDrumPickerFormFeature } from '@mindly/ui-components';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../redux/user/selector';
import { updateUserPersonalData } from '../../api/user';
import { setUser } from '../../redux/user/reducer';
import useToast from '../../context/ToastContext/ToastContext';
import { useAnalytics } from 'src/shared';
import { useIonViewDidEnter } from '@ionic/react';

type ProfileDoBWidgetProps = {
  onSuccess?: (dob: string) => void;
};

const ProfileDoBWidget = ({ onSuccess }: ProfileDoBWidgetProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const onSuccessCbRef = useRef(onSuccess);
  const user = useSelector(userSelector);
  const { trackPredefinedEvent } = useAnalytics();

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  useIonViewDidEnter(() => {
    trackPredefinedEvent('SIGNUP_USER_INFO_VIEWED', {
      screen_type: 'birthday',
    });
  });

  useLayoutEffect(() => {
    onSuccessCbRef.current = onSuccess;
  });

  const handleSubmit = useCallback(
    async (value: Record<string, string>) => {
      try {
        if (!DateTime.fromFormat(`${value.day}.${value.month}.${value.year}`, 'dd.MM.yyyy').isValid) {
          throw new Error(t('app:errors.date_invalid'));
        }
        setIsLoading(true);
        const { data, error, message } = await updateUserPersonalData({
          dob: `${value.day}.${value.month}.${value.year}`,
        });

        if (error) {
          throw new Error(message ? t(`profile:${message}`) : 'Something went wrong');
        }

        dispatch(setUser(data));

        trackPredefinedEvent('SIGNUP_USER_INFO_SUBMITTED', {
          screen_type: 'birthday',
          value: data.dob,
          need_to_update: true,
        });

        onSuccessCbRef.current?.(data.dob);
      } catch (e: any) {
        showToast({ text: e?.message });
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccessCbRef, t, DateTime]
  );

  return (
    <>
      <ScreenDrumPickerFormFeature
        title={t('profile:date_of_birth.title')}
        subtitle={t('profile:personal_details.date_of_birth.description')}
        compareBy={'value'}
        selections={{
          day: Array.from({ length: 31 }, (_, i) => ({
            value: String(i + 1).padStart(2, '0'),
            label: String(i + 1).padStart(2, '0'),
          })),
          month: Array.from({ length: 12 }, (_, i) => ({
            value: DateTime.fromFormat((i + 1).toString(), 'M').toFormat('MM'),
            label: DateTime.local()
              .set({ month: i + 1 })
              .setLocale(language)
              .toFormat('LLLL'),
          })),
          year: Array.from({ length: DateTime.local().year - 18 - 1940 + 1 }, (_, i) => ({
            value: (1940 + i).toString(),
            label: (1940 + i).toString(),
          })),
        }}
        defaults={{
          day: user?.dob ? DateTime.fromFormat(user.dob, 'dd.MM.yyyy').toFormat('dd') : '01',
          month: user?.dob ? DateTime.fromFormat(user.dob, 'dd.MM.yyyy').toFormat('MM') : '01',
          year: user?.dob
            ? DateTime.fromFormat(user.dob, 'dd.MM.yyyy').toFormat('yyyy')
            : (DateTime.local().year - 23).toString(),
        }}
        onChange={handleSubmit}
        isLoading={isLoading}
        buttonText={t('profile:date_of_birth.button')}
      />
    </>
  );
};

export default ProfileDoBWidget;
